export const API_BASE_URL = 'https://lillaflora.nu/api';

// API Secret Token for admin operations - keep this secure!
// This should be the same token as set in backend/config/api_security.php
const API_SECRET_TOKEN = '8f2e4c7b9d1a6f3e8b2c5d9f7a4e6b8c1d9f2e7a5b8c3f6e9d2a7b4e5c8f1a6d3b9e2c7f4a5d8b1e6c3f9d2a7e4b5c8f6a1d9e2b7c4f5a8d1b3e6c9f2a5d7e4b1c8f6a3d9e2b5c7f4a8d1e6c3f9d2a7e4b5c8f6a1d9e2b7c4f5';

// Helper function to get auth headers for admin requests
function getAuthHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${API_SECRET_TOKEN}`
  };
}

// Normalize image URL coming from API or uploader to a usable <img src>
// Accepts values like:
// - full URLs: http(s)://host/api/image/filename -> returned as-is
// - API paths: /api/image/filename -> prefixed with host
// - bare filenames: filename -> prefixed with host + /api/image/
export function toImageSrc(image: string | undefined | null): string {
  if (!image) return '';
  const str = String(image);
  if (str.startsWith('http://') || str.startsWith('https://')) return str;
  const baseHost = API_BASE_URL.replace(/\/api$/i, '');
  if (str.startsWith('/api/image/')) return `${baseHost}${str}`;
  return `${API_BASE_URL}/image/${str}`;
}

// Prefer square thumbnail variant when showing grid/list items
export function toThumbSrc(image: string | undefined | null): string {
  if (!image) return '';
  const baseHost = API_BASE_URL.replace(/\/api$/i, '');
  // Extract filename
  let filename = '';
  const str = String(image);
  if (str.startsWith('http://') || str.startsWith('https://')) {
    const url = new URL(str);
    const parts = url.pathname.split('/');
    filename = parts[parts.length - 1] || '';
  } else if (str.startsWith('/api/image/')) {
    filename = str.substring('/api/image/'.length);
  } else {
    filename = str;
  }

  if (!filename) return toImageSrc(image);

  // Build thumb filename: ensure it starts with thumb_
  let thumbName = filename;
  if (thumbName.startsWith('opt_')) {
    thumbName = 'thumb_' + thumbName.substring(4);
  } else if (!thumbName.startsWith('thumb_')) {
    thumbName = 'thumb_' + thumbName;
  }

  return `${baseHost}/api/image/${thumbName}`;
}

interface Product {
  id?: number;
  name: string;
  slug: string;
  description: string;
  short_description: string;
  price: number;
  compare_price?: number | null;
  cost_price?: number | null;
  final_price?: number | null;
  discount_type?: 'fixed' | 'percentage';
  discount_percentage?: number;
  discount_amount?: number;
  discount_label?: string;
  has_active_discount?: boolean;
  sku: string;
  inventory_quantity: number;
  is_active: boolean;
  is_featured: boolean;
  category_ids?: number[];
  categories?: string[];
  category_slugs?: string[];
  images?: string[];
  created_at?: string;
  updated_at?: string;
}

interface ApiResponse {
  message?: string;
  id?: number;
}

interface OrderStatistics {
  not_shipped: number;
  shipped_today: number;
  shipped_this_week: number;
  shipped_this_month: number;
}

interface Order {
  id: number;
  order_number: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  billing_address: string;
  shipping_address?: string;
  total_amount: number;
  shipping_cost: number;
  tax_amount: number;
  status: 'pending' | 'sent' | 'cancelled';
  payment_method?: string;
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  notes?: string;
  created_at: string;
  updated_at: string;
  shipped_at?: string | null;
  net_profit?: number;
}

interface OrderItem {
  id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  price: number;
  total_price: number;
}

interface OrderDetails extends Order {
  campaign_code?: string | null;
  campaign_discount_percentage?: number;
  campaign_discount_amount?: number;
  items: OrderItem[];
}

class ApiService {
  // Helper method to handle API responses
  private async handleResponse(response: Response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  // Product methods
  async getProducts(params?: {
    category?: string;
    featured?: boolean;
    limit?: number;
    offset?: number;
    search?: string;
    includeAuth?: boolean;
  }): Promise<Product[]> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const url = `${API_BASE_URL}/products${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetch(url);
    return this.handleResponse(response);
  }

  async getProduct(id: string | number): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}/products/${id}`);
    return this.handleResponse(response);
  }

  async getRelatedProducts(id: number, limit: number = 4): Promise<Product[]> {
    const searchParams = new URLSearchParams();
    if (limit) {
      searchParams.append('limit', String(limit));
    }
    const response = await fetch(
      `${API_BASE_URL}/products/${id}/related${searchParams.toString() ? '?' + searchParams.toString() : ''}`
    );
    return this.handleResponse(response);
  }

  async createProduct(product: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(product),
    });
    return this.handleResponse(response);
  }

  async updateProduct(id: number, product: Partial<Product>): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(product),
    });
    return this.handleResponse(response);
  }

  async deleteProduct(id: number): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Category methods
  async getCategories(params?: { with_count?: boolean }): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (params?.with_count) {
      searchParams.append('with_count', 'true');
    }

    const url = `${API_BASE_URL}/categories${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetch(url);
    return this.handleResponse(response);
  }

  async getCategory(id: string | number): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/categories/${id}`);
    return this.handleResponse(response);
  }

  async getCategoryBySlug(slug: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/categories/${slug}`);
    return this.handleResponse(response);
  }

  async createCategory(category: any): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/categories`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(category)
    });
    return this.handleResponse(response);
  }

  async updateCategory(id: number, category: any): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(category)
    });
    return this.handleResponse(response);
  }

  async deleteCategory(id: number): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Utility method to generate slug from name
  generateSlug(name: string): string {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[åä]/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .trim()
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-'); // Remove duplicate hyphens
  }

  // Calculate margin percentage
  calculateMargin(sellPrice: number, costPrice: number): number {
    if (!costPrice || costPrice === 0) return 0;
    return Math.round(((sellPrice - costPrice) / sellPrice) * 100);
  }

  // Calculate markup percentage
  calculateMarkup(sellPrice: number, costPrice: number): number {
    if (!costPrice || costPrice === 0) return 0;
    return Math.round(((sellPrice - costPrice) / costPrice) * 100);
  }

  // Order methods
  async getOrderStatistics(): Promise<OrderStatistics> {
    const response = await fetch(`${API_BASE_URL}/orders/statistics`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getOrders(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    search?: string;
    sort_by?: string;
    sort_order?: 'ASC' | 'DESC';
  }): Promise<Order[]> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const url = searchParams.toString() 
      ? `${API_BASE_URL}/orders?${searchParams.toString()}`
      : `${API_BASE_URL}/orders`;
    
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async updateOrderStatus(orderId: number, status: string, shippedAt?: string): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/status`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status, shipped_at: shippedAt })
    });
    return this.handleResponse(response);
  }

  async deleteOrder(id: number): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/orders/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async getOrderDetails(id: number): Promise<OrderDetails> {
    const response = await fetch(`${API_BASE_URL}/orders/${id}`, {
      headers: getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  async sendOrderToScanning(orderId: number): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/send-for-scanning`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getOrderSalesTimeline(period: 'last_30_days' | 'last_12_weeks' | 'last_12_months'): Promise<{ labels: string[]; orders: number[]; revenue: number[]; raw: any[] }> {
    const searchParams = new URLSearchParams();
    if (period) {
      searchParams.append('period', period);
    }

    const url = `${API_BASE_URL}/orders/sales${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async uploadImage(file: File): Promise<{ filename: string; url: string }> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${API_SECRET_TOKEN}`
      },
      body: formData
    });
    return this.handleResponse(response);
  }

  // Stripe: get publishable key
  async getStripeConfig(): Promise<{ publishable_key: string }> {
    const response = await fetch(`${API_BASE_URL}/stripe/config`);
    return this.handleResponse(response);
  }

  // Stripe: create PaymentIntent for checkout
  async createPaymentIntent(data: any): Promise<{ client_secret: string; payment_intent_id: string }> {
    const response = await fetch(`${API_BASE_URL}/stripe/create-payment-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  // Orders: create new order
  async createOrder(order: any): Promise<ApiResponse & { email_sent?: boolean }> {
    const response = await fetch(`${API_BASE_URL}/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(order)
    });
    return this.handleResponse(response);
  }

  // Visitor Analytics methods (admin only)
  async getVisitorStatistics(days: number = 30): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/visitor?days=${days}`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getBounceAnalytics(days: number = 30): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/visitor/bounce?days=${days}`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getMostVisitedProducts(limit: number = 10, days: number = 30): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/visitor/products?limit=${limit}&days=${days}`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getMostVisitedPages(limit: number = 10, days: number = 30): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/visitor/pages?limit=${limit}&days=${days}`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getMarketingPageStats(days: number = 30): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/visitor/marketing?days=${days}`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async sendContactMessage(payload: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    return this.handleResponse(response);
  }

  async getDeviceStats(days: number = 30): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/visitor/devices?days=${days}`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getReferrerStats(days: number = 30): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/visitor/referrers?days=${days}`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getConversionStats(days: number = 30): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/visitor/conversions?days=${days}`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // SEO methods (admin only)
  async getSEOSettings(entityType?: string, entityId?: number): Promise<any> {
    let url = `${API_BASE_URL}/seo?with_details=true`;
    if (entityType && entityId !== undefined) {
      url += `&entity_type=${entityType}&entity_id=${entityId}`;
    } else if (entityType) {
      url += `&entity_type=${entityType}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async saveSEOSettings(seoData: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/seo`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(seoData)
    });
    return this.handleResponse(response);
  }

  async updateSEOSettings(id: number, seoData: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/seo/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(seoData)
    });
    return this.handleResponse(response);
  }

  async deleteSEOSettings(id: number): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/seo/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getSEOForEntity(entityType: string, entityId: number | string): Promise<SEOSettings | null> {
    const response = await fetch(`${API_BASE_URL}/seo?entity_type=${entityType}&entity_id=${entityId}`, {
      headers: getAuthHeaders()
    });
    const result = await this.handleResponse(response);
    return result.length > 0 ? result[0] : null;
  }

  async getSEOStatistics(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/seo/statistics`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Customer methods (admin only)
  async getCustomers(params?: {
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: 'ASC' | 'DESC';
  }): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const url = `${API_BASE_URL}/customers${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getCustomerOrders(email: string): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/customers/${encodeURIComponent(email)}/orders`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getCustomerStatistics(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/customers/statistics`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async sendBulkEmail(emailData: {
    emails: Array<{email: string; first_name?: string; last_name?: string}>;
    subject: string;
    content: string;
    sender_name?: string;
  }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/customers/send-email`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(emailData)
    });
    return this.handleResponse(response);
  }

  async deleteCustomer(email: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/customers/${encodeURIComponent(email)}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getProductStatistics(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/products/statistics`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  async getTopProducts(limit: number = 10): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/products/top?limit=${limit}`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Generic GET method for discount endpoints
  async get(endpoint: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Generic POST method
  async post(endpoint: string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  // Generic PUT method
  async put(endpoint: string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  // Generic DELETE method
  async delete(endpoint: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return this.handleResponse(response);
  }
}

interface SEOSettings {
  id?: number;
  entity_type: string;
  entity_id: number | string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  meta_image?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  canonical_url?: string;
  robots_meta?: string;
  noindex?: boolean;
  nofollow?: boolean;
  created_at?: string;
  updated_at?: string;
}

export default new ApiService();
export type { Product, ApiResponse, OrderStatistics, Order, OrderDetails, OrderItem, SEOSettings };
